import { formatINR, targetVariance } from "@/lib/utils";

// Per-metric config: which direction is good, the display precision, and how the
// target + gap are formatted. Centralised here (not repeated at each callsite) so
// the flock card, comparison table, and farm P&L render a target the same way.
const METRIC = {
  hdp: {
    higherIsBetter: true,
    decimals: 1,
    // Round the goal to the same 1dp the gap uses so they reconcile — a value like
    // 85.567 (only reachable via a hand-edited JSONB) shows "85.6%", not "85.567%".
    // Number() strips a trailing ".0" so a clean 85 still reads "85%".
    formatTarget: (t: number) => `${Number(t.toFixed(1))}%`,
    formatMagnitude: (m: number) => `${m.toFixed(1)}pt`,
  },
  cost_per_egg: {
    higherIsBetter: false,
    decimals: 2,
    formatTarget: (t: number) => formatINR(t, 2),
    formatMagnitude: (m: number) => formatINR(m, 2),
  },
  fcr: {
    // Feed conversion ratio — unitless (kg feed / kg eggs), lower is better.
    higherIsBetter: false,
    decimals: 2,
    formatTarget: (t: number) => t.toFixed(2),
    formatMagnitude: (m: number) => m.toFixed(2),
  },
  mortality_pct: {
    higherIsBetter: false,
    decimals: 1,
    formatTarget: (t: number) => `${Number(t.toFixed(1))}%`,
    formatMagnitude: (m: number) => `${m.toFixed(1)}pt`,
  },
} as const;

/**
 * Inline "vs target" read-out shared by the flock card, comparison table, and
 * farm P&L. Renders nothing when there's no target to compare against (so every
 * surface stays exactly as before until the owner sets a goal in Farm Settings).
 *
 * The arrow shows which side of the (display-rounded) target the actual sits on
 * (▲ above / ▼ below / ● on); the colour shows good vs bad (green = meeting the
 * goal), so a cost overrun (▲, red) and an HDP beat (▲, green) read correctly
 * despite the same arrow. Glyph and magnitude both derive from the rounded delta,
 * so a gap that rounds to zero shows "● 0.0pt", never a misleading "▲ 0.0pt".
 */
export default function TargetVarianceBadge({
  actual,
  target,
  metric,
  label = "target",
  className = "",
}: {
  actual: number | null;
  target: number | null | undefined;
  metric: keyof typeof METRIC;
  // The reference this compares against. Defaults to the owner's "target"; the
  // breed-standard callsite passes "breed standard".
  label?: string;
  className?: string;
}) {
  const cfg = METRIC[metric];
  const v = targetVariance(actual, target, cfg.higherIsBetter, cfg.decimals);
  if (v === null) return null;
  const glyph = v.delta > 0 ? "▲" : v.delta < 0 ? "▼" : "●";
  return (
    <span className={className}>
      {label} <span className="font-medium">{cfg.formatTarget(target as number)}</span>{" "}
      <span className={v.toneClass}>
        {glyph} {cfg.formatMagnitude(Math.abs(v.delta))}
      </span>
    </span>
  );
}
